/**
 * Comprehensive validation utilities for tax filing application
 */

// SSN Validation: Exactly 9 digits
export const validateSSN = (ssn) => {
  const cleaned = ssn.replace(/[^0-9]/g, '');
  return {
    isValid: cleaned.length === 9,
    cleaned: cleaned,
    formatted: cleaned.length === 9 
      ? `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5)}` 
      : cleaned,
    error: cleaned.length !== 9 ? 'SSN must be exactly 9 digits' : null
  };
};

// EIN Validation: Exactly 9 digits
export const validateEIN = (ein) => {
  const cleaned = ein.replace(/[^0-9]/g, '');
  return {
    isValid: cleaned.length === 9,
    cleaned: cleaned,
    formatted: cleaned.length === 9 
      ? `${cleaned.slice(0, 2)}-${cleaned.slice(2)}` 
      : cleaned,
    error: cleaned.length !== 9 ? 'EIN must be exactly 9 digits' : null
  };
};

// Date of Birth Validation: Must be in the past
export const validateDOB = (date) => {
  if (!date) return { isValid: false, error: 'Date of birth is required' };
  
  const selectedDate = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to compare dates only
  
  return {
    isValid: selectedDate < today,
    error: selectedDate >= today ? 'Date of birth must be in the past' : null
  };
};

// Name Validation: Alphabetic characters, spaces, hyphens, apostrophes only
export const validateName = (name) => {
  if (!name || name.trim().length === 0) {
    return { isValid: false, error: 'Name is required' };
  }
  
  // Allow letters (including accented), spaces, hyphens, apostrophes
  const nameRegex = /^[a-zA-ZÀ-ÿ\s'\-]+$/;
  const isValid = nameRegex.test(name);
  
  return {
    isValid,
    cleaned: name.trim(),
    error: !isValid ? 'Name can only contain letters, spaces, hyphens, and apostrophes' : null
  };
};

// Zip Code Validation: 5 digits or ZIP+4 format (99999-9999)
export const validateZipCode = (zip) => {
  const cleaned = zip.replace(/[^0-9-]/g, '');
  const digitsOnly = cleaned.replace(/-/g, '');
  
  // Accept 5 digits or 9 digits (ZIP+4)
  const isValid = digitsOnly.length === 5 || digitsOnly.length === 9;
  
  let formatted = cleaned;
  if (digitsOnly.length === 9 && !cleaned.includes('-')) {
    formatted = `${digitsOnly.slice(0, 5)}-${digitsOnly.slice(5)}`;
  }
  
  return {
    isValid,
    cleaned: digitsOnly,
    formatted,
    error: !isValid ? 'Zip code must be 5 digits or ZIP+4 format (12345-6789)' : null
  };
};

// Phone Number Validation: 10 digits
export const validatePhoneNumber = (phone) => {
  const cleaned = phone.replace(/[^0-9]/g, '');
  
  return {
    isValid: cleaned.length === 10,
    cleaned,
    formatted: cleaned.length === 10 
      ? `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}` 
      : cleaned,
    error: cleaned.length !== 10 ? 'Phone number must be 10 digits' : null
  };
};

// Numeric Only Input Handler
export const handleNumericInput = (value, maxLength) => {
  const cleaned = value.replace(/[^0-9]/g, '');
  return maxLength ? cleaned.slice(0, maxLength) : cleaned;
};

// Alphabetic Only Input Handler
export const handleAlphabeticInput = (value) => {
  return value.replace(/[^a-zA-ZÀ-ÿ\s'\-]/g, '');
};

// US States List
export const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

// Form validation helper
export const validateRequiredFields = (fields) => {
  const errors = {};
  Object.keys(fields).forEach(key => {
    if (!fields[key] || (typeof fields[key] === 'string' && fields[key].trim() === '')) {
      errors[key] = 'This field is required';
    }
  });
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
